import React, { useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  CloseIcon,
  DownloadIcon,
  EditIcon,
  VisibilityIcon,
} from "../../../icons";
import { DocumentEditor } from "./DocumentEditor";
import { downloadWithAuth } from "../../../common/utils/downloadWithAuth";
import { FieldLabelWithHelp } from "./FieldHelpIcon";

export type InputFileProps = {
  label: string;
  fileData?: any;
  downloadUrl?: string;
  onContentChange?: (content: string) => void;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  editMode?: "html" | "text";
  hidden?: boolean;
  description?: string;
};

export const InputFile: React.FC<InputFileProps> = observer(
  ({
    label,
    fileData,
    downloadUrl,
    onContentChange,
    error,
    disabled = false,
    readOnly = false,
    editMode = "html",
    hidden = false,
    description,
  }) => {
    if (hidden) return null;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editorContent, setEditorContent] = useState("");
    const [templateContent, setTemplateContent] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const hasFile = fileData && fileData.length > 0;

    const handleOpenDialog = async () => {
      if (editMode === "html" && hasFile) {
        setIsLoading(true);
        try {
          // Load file content for editing
          const response = await fetch(downloadUrl || "", {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });

          if (response.ok) {
            const content = await response.text();
            setEditorContent(content);
            setTemplateContent(content);
          }
        } catch (error) {
          console.error("Error loading file content:", error);
        } finally {
          setIsLoading(false);
        }
      }
      setDialogOpen(true);
    };

    const handleCloseDialog = () => {
      setDialogOpen(false);
      setEditorContent("");
      setTemplateContent("");
    };

    const handleSave = () => {
      if (onContentChange && templateContent) {
        onContentChange(templateContent);
      }
      handleCloseDialog();
    };

    const handleDownload = async () => {
      if (hasFile && downloadUrl) {
        const fileName = fileData[0].name;
        await downloadWithAuth(downloadUrl, fileName);
      }
    };

    const handleTemplateChange = (newContent: string) => {
      setTemplateContent(newContent);
    };

    return (
      <Box sx={{ width: "100%" }}>
        <Typography
          variant="body2"
          color="textSecondary"
          gutterBottom
          component="div"
        >
          <FieldLabelWithHelp label={label} description={description} />
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          {hasFile && (
            <>
              <Typography variant="body1" sx={{ flex: 1 }}>
                {fileData[0].name}
              </Typography>

              {!readOnly && editMode === "html" && (
                <Tooltip title="Editar HTML">
                  <IconButton
                    size="small"
                    onClick={handleOpenDialog}
                    disabled={disabled}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Ver">
                <IconButton
                  size="small"
                  onClick={handleOpenDialog}
                  disabled={disabled}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Descargar">
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  disabled={disabled}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          {!hasFile && (
            <Typography variant="body2" color="textSecondary">
              No hay archivo disponible
            </Typography>
          )}
        </Stack>

        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}

        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6">
                {readOnly ? "Ver" : "Editar"} {label}
              </Typography>
              <IconButton onClick={handleCloseDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent dividers>
            {isLoading ? (
              <Typography>Cargando contenido...</Typography>
            ) : (
              editMode === "html" && (
                <DocumentEditor
                  renderedContent={editorContent}
                  templateContent={templateContent}
                  opened={true}
                  initialMode={readOnly ? "readonly" : "edit"}
                  onTemplateChange={handleTemplateChange}
                />
              )
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            {!readOnly && (
              <Button onClick={handleSave} variant="contained" color="primary">
                Guardar
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);
